import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from '@/hooks/useTranslation';

export interface PersonaConfig {
  persona: 'controller' | 'analyst' | 'ceo' | 'cfo';
  userRole: string;
  displayName: string;
  description: string;
  color: string;
  icon: string;
  priorities: string[];
  kpis: string[];
  valueProposition: 'get_paid_faster' | 'grow_cross_border' | 'qualify_for_contracts';
  businessOutcome: {
    title: string;
    value: number;
    description: string;
  };
  dashboardComponents: {
    'section-key-changes': string[];
    'section-whats-driving-it': string[];
    'section-take-action': string[];
  };
  navigationItems: string[];
  permissions: {
    canApprovePayments: boolean;
    canViewAnalytics: boolean;
    canManageUsers: boolean;
    canExportData: boolean;
    canConfigureAI: boolean;
  };
}

/**
 * BUG-90 — this object, not `useDashboardConfig` or `DragDropProvider`, is what
 * the dashboard actually renders. `Dashboard.tsx` feeds each `<ExpandableSection>`
 * from `getDashboardComponents(section)` below, so `dashboardComponents` IS the
 * default layout a demo viewer sees on first load.
 *
 * Three other places in the tree also declare a default layout and all three put
 * `scenario-planning` in "What's Driving It" — `SectionLayout.tsx` (never
 * imported), `DragDropProvider.tsx:whatsDrivingItChartOrder` (consumed only by
 * GridLayout) and `useDashboardConfig.getPersonalizedComponents` (consumed only
 * by DashboardPanels for metadata). None of them reach the dashboard. This one
 * had been left at one widget per section, which is why the Scenario Planning
 * panel was only reachable via "Agregar Gráficos" and the reporter had to add it
 * himself mid-recording ("when we go in and we start to add in graphics").
 */
export const PERSONA_CONFIGS: Record<string, PersonaConfig> = {
  controller: {
    persona: 'controller',
    userRole: 'controller',
    displayName: 'persona.financialController',
    description: 'Operational focus on cash flow and payment acceleration',
    color: 'blue',
    icon: 'DollarSign',
    priorities: ['Cash Flow Management', 'Payment Acceleration', 'Account Reconciliation'],
    kpis: ['DSO', 'Working Capital', 'Payment Velocity'],
    valueProposition: 'get_paid_faster',
    businessOutcome: {
      title: 'business.getPaidFaster',
      value: 180000,
      description: 'business.getPaidFasterDesc'
    },
    dashboardComponents: {
      'section-key-changes': ['kpi-bar'],
      'section-whats-driving-it': ['budget-actuals', 'scenario-planning'],
      'section-take-action': ['ai-recommendations']
    },
    navigationItems: ['receivables', 'payables', 'payments-in', 'payments-out', 'approvals'],
    permissions: {
      canApprovePayments: true,
      canViewAnalytics: true,
      canManageUsers: false,
      canExportData: true,
      canConfigureAI: false
    }
  },
  analyst: {
    persona: 'analyst',
    userRole: 'analyst',
    displayName: 'Business Analyst',
    description: 'Data analysis and predictive insights',
    color: 'purple',
    icon: 'BarChart3',
    priorities: ['Data Quality', 'Trend Analysis', 'Predictive Modeling'],
    kpis: ['Data Accuracy', 'Model Performance', 'Insight Generation'],
    valueProposition: 'grow_cross_border',
    businessOutcome: {
      title: 'Scale Cross-Border Operations Efficiently',
      value: 800000,
      description: 'Automated compliance and FX optimization for manufacturing growth'
    },
    dashboardComponents: {
      'section-key-changes': ['kpi-bar'],
      'section-whats-driving-it': ['budget-actuals', 'scenario-planning'],
      'section-take-action': ['ai-recommendations']
    },
    navigationItems: ['analytics', 'forex', 'usmca-compliance', 'documents'],
    permissions: {
      canApprovePayments: false,
      canViewAnalytics: true,
      canManageUsers: false,
      canExportData: true,
      canConfigureAI: true
    }
  },
  ceo: {
    persona: 'ceo',
    userRole: 'ceo',
    displayName: 'Chief Executive',
    description: 'Strategic oversight and growth opportunities',
    color: 'green',
    icon: 'TrendingUp',
    priorities: ['Strategic Planning', 'Growth Opportunities', 'Risk Management'],
    kpis: ['Revenue Growth', 'Market Position', 'Strategic Progress'],
    valueProposition: 'qualify_for_contracts',
    businessOutcome: {
      title: 'Qualify for 40% Larger Contracts',
      value: 2300000,
      description: 'Financial intelligence to win bigger manufacturing contracts'
    },
    dashboardComponents: {
      'section-key-changes': ['kpi-bar'],
      'section-whats-driving-it': ['budget-actuals', 'scenario-planning'],
      'section-take-action': ['ai-recommendations']
    },
    navigationItems: ['dashboard', 'analytics', 'suppliers', 'buyers', 'settings'],
    permissions: {
      canApprovePayments: true,
      canViewAnalytics: true,
      canManageUsers: true,
      canExportData: true,
      canConfigureAI: true
    }
  },
  cfo: {
    persona: 'cfo',
    userRole: 'cfo',
    displayName: 'Chief Financial Officer',
    description: 'Strategic focus on compliance, reporting, and cash flow',
    color: 'indigo',
    icon: 'BarChart3',
    priorities: ['Financial Reporting', 'Compliance & Risk', 'Cash Flow Management'],
    kpis: ['Financial Health', 'Compliance Score', 'Cash Flow'],
    valueProposition: 'qualify_for_contracts',
    businessOutcome: {
      title: 'Strategic Financial Leadership',
      value: 1500000,
      description: 'Executive financial leadership and strategy'
    },
    dashboardComponents: {
      'section-key-changes': ['kpi-bar'],
      'section-whats-driving-it': ['budget-actuals', 'scenario-planning'],
      'section-take-action': ['ai-recommendations']
    },
    navigationItems: ['dashboard', 'analytics', 'forex', 'settings'],
    permissions: {
      canApprovePayments: true,
      canViewAnalytics: true,
      canManageUsers: true,
      canExportData: true,
      canConfigureAI: true
    }
  }
};

export function useCentralizedPersona() {
  const { t } = useTranslation();
  const [currentPersonaKey, setCurrentPersonaKey] = useState<string>('controller');
  
  // Get persona from localStorage on mount
  useEffect(() => {
    try {
      const storedPersona = localStorage.getItem('detectedPersona');
      const onboardingData = localStorage.getItem('onboardingData');
      
      if (storedPersona) {
        const personaData = JSON.parse(storedPersona);
        setCurrentPersonaKey(personaData.persona || 'controller');
      } else if (onboardingData) {
        const onboarding = JSON.parse(onboardingData);
        setCurrentPersonaKey(onboarding.userRole || 'controller');
      }
    } catch (error) {
      console.error('Error loading persona:', error);
      setCurrentPersonaKey('controller');
    }
  }, []);
  
  // Get the current persona configuration
  const currentPersona = useMemo(() => {
    return PERSONA_CONFIGS[currentPersonaKey] || PERSONA_CONFIGS.controller;
  }, [currentPersonaKey]);

  // Check if user has permission for specific actions
  const hasPermission = useMemo(() => {
    return (permission: keyof PersonaConfig['permissions']) => {
      return currentPersona.permissions[permission];
    };
  }, [currentPersona]);

  // Get persona-specific navigation items
  const getNavigationItems = useMemo(() => {
    return () => {
      return currentPersona.navigationItems;
    };
  }, [currentPersona]);

  // Get persona-specific dashboard components
  const getDashboardComponents = useMemo(() => {
    return (section: keyof PersonaConfig['dashboardComponents']) => {
      return currentPersona.dashboardComponents[section] || [];
    };
  }, [currentPersona]);

  // Get persona-specific business outcome
  const getBusinessOutcome = useMemo(() => {
    return () => {
      return currentPersona.businessOutcome;
    };
  }, [currentPersona]);

  // Check if current view is appropriate for persona
  const isViewAppropriate = useMemo(() => {
    return (view: string) => {
      const allowedViews = currentPersona.navigationItems;
      return allowedViews.includes(view) || view === 'dashboard';
    };
  }, [currentPersona]);

  // Get persona-specific messaging
  const getPersonaMessage = useMemo(() => {
    return (messageType: 'welcome' | 'insight' | 'action') => {
      const messageKey = `persona.${currentPersona.persona}.${messageType}`;
      const fallbackKey = `persona.controller.${messageType}`;
      
      return t(messageKey, t(fallbackKey, ''));
    };
  }, [currentPersona, t]);

  return {
    // Current persona state
    persona: currentPersona.persona,
    userRole: currentPersona.userRole,
    displayName: t(currentPersona.displayName, 'Financial Controller'),
    description: currentPersona.description,
    color: currentPersona.color,
    icon: currentPersona.icon,
    priorities: currentPersona.priorities,
    kpis: currentPersona.kpis,
    valueProposition: currentPersona.valueProposition,
    businessOutcome: {
      title: t(currentPersona.businessOutcome.title, currentPersona.businessOutcome.title),
      value: currentPersona.businessOutcome.value,
      description: t(currentPersona.businessOutcome.description, currentPersona.businessOutcome.description)
    },
    
    // Permission checks
    hasPermission,
    
    // Navigation and components
    getNavigationItems,
    getDashboardComponents,
    isViewAppropriate,
    
    // Business logic
    getBusinessOutcome,
    getPersonaMessage,
    
    // Raw configuration
    config: currentPersona,
    
    // Context information
    isLoaded: true,
    
    // Persona management
    setCurrentPersonaKey
  };
} 